import { Subject } from 'rxjs';
import { PROTO_BAUD, StreamSplitter } from './eduProtocol';
import { logProtocolError } from './traceabilityLog';

/**
 * Cuánto se espera a cada paso del cierre antes de seguir sin él.
 *
 * Los cierres de WebSerial pueden quedarse esperando al dispositivo: close() del
 * puerto no resuelve hasta que el stream termina de cerrarse del otro lado, y con
 * una placa que no está mandando nada eso puede tardar indefinidamente -- en la
 * práctica, hasta que alguien la desenchufa.
 *
 * Nada de lo que viene después depende de que el cierre haya terminado bien: los
 * locks ya se soltaron antes, y quien pidió el puerto (el flasheo) lo abre de
 * nuevo por su cuenta. Entonces se le da un rato y se sigue, en vez de dejar la
 * app esperando algo que quizá no llegue.
 */
const CLOSE_STEP_TIMEOUT_MS = 3000;

/**
 * Cuántas veces se insiste con abrir el puerto, y cada cuánto.
 *
 * Abrir puede chocar con quien lo acaba de soltar. El flasheo es el caso: avrgirl
 * llama a su callback cuando terminó de escribir, pero su close() sigue en camino,
 * y en esa ventana el puerto ya no tiene readable y todavía no está cerrado -- así
 * que open() tira InvalidStateError diciendo que ya está abierto. No es un error
 * del que haya que avisar: es llegar temprano, y lo único que corresponde es
 * volver a intentar en un momento.
 */
const OPEN_ATTEMPTS = 5;
const OPEN_RETRY_MS = 200;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Espera una operación de cierre con límite, y deja anotado en la consola cuál
 * fue la que se pasó de tiempo. Ese dato es lo único que después dice qué parte
 * del cierre se trabó con una placa de verdad.
 */
const guard = async (operation, label, timeout) => {
  let timer = null;
  const timedOut = await Promise.race([
    Promise.resolve().then(operation).then(() => false, () => false),
    new Promise((resolve) => { timer = setTimeout(() => resolve(true), timeout); }),
  ]);
  clearTimeout(timer);
  if (timedOut) logProtocolError('', `${label} no terminó en ${timeout}ms; se sigue sin esperarlo.`);
  return timedOut;
};

/**
 * Dueño único de un puerto WebSerial.
 *
 * WebSerial deja abrir un puerto una sola vez y entregar el lock de lectura a un
 * solo reader. Hoy hay tres lugares que lo abren por su cuenta -- el monitor
 * serie, el flasheo y ahora el ping de trazabilidad -- y el segundo que llega
 * falla. Esta clase abre una vez, lee una vez, y reparte lo que llega.
 *
 * Lo que se reparte ya viene separado: el stream se corta en tramas binarias del
 * protocolo por un lado y texto del sketch del usuario por el otro. Así el
 * monitor no necesita saber nada de nuestros pings para no mostrarlos.
 *
 * Los interesados se anotan con acquire() y se van con release(); el puerto se
 * cierra recién cuando no queda ninguno. El flasheo es el caso aparte: necesita
 * el puerto para él solo, así que withExclusiveAccess() cierra la sesión, lo deja
 * hacer, y la vuelve a abrir si todavía hay alguien esperando.
 */
class SerialSession {
  constructor(port, { baudRate = PROTO_BAUD, closeTimeout = CLOSE_STEP_TIMEOUT_MS } = {}) {
    this.port = port;
    this.baudRate = baudRate;
    this.closeTimeout = closeTimeout;
    this.owners = new Set();
    this.opened = false;
    this.reader = null;
    this.writer = null;
    this.readLoop = null;
    // Guarda lo que quedó a medias entre dos chunks: una trama partida, un MAGIC
    // suelto, un carácter UTF-8 cortado al medio.
    this.splitter = new StreamSplitter();
    // Mientras esto está en true el puerto es de otro (el flasheo) y no se abre.
    this.suspended = false;
    // Las dos salidas del demultiplexor.
    this.text = new Subject();
    this.frames = new Subject();
    // Las aperturas y cierres se encadenan acá: sin esto, un acquire y un release
    // que se pisan dejan el puerto medio abierto y sin nadie leyéndolo.
    this.queue = Promise.resolve();
  }

  // Encola una operación sobre el puerto detrás de las anteriores.
  enqueue = (operation) => {
    this.queue = this.queue.then(operation, operation);
    return this.queue;
  }

  get isOpen() { return this.opened; }

  // Si el puerto está prestado a otro (el flasheo). Lo mira el ping, que mientras
  // tanto no tiene a quién preguntarle.
  get isSuspended() { return this.suspended; }

  get hasOwners() { return this.owners.size > 0; }

  /**
   * Anota un interesado y abre el puerto si hacía falta. El mismo owner puede
   * pedirlo dos veces sin abrir dos veces: es un conjunto, no un contador, así
   * que un release suelto tampoco cierra el puerto de otro.
   */
  acquire = (owner) => {
    this.owners.add(owner);
    return this.enqueue(() => this.openPort());
  }

  release = (owner) => {
    this.owners.delete(owner);
    if (this.hasOwners) return Promise.resolve();
    return this.enqueue(() => this.closePort());
  }

  openPort = async () => {
    if (this.opened || this.suspended || !this.hasOwners) return;
    /**
     * Un puerto que ya está abierto se toma como está en vez de volver a abrirlo:
     * open() sobre uno abierto tira InvalidStateError, y esto pasa de verdad
     * cuando el monitor lo dejó abierto por un camino viejo.
     */
    if (!this.port.readable && !await this.openWithRetry()) return;
    this.opened = true;
    // Lo que quedó a medias de la apertura anterior no se completa con esta.
    this.splitter.reset();
    this.readLoop = this.read();
  }

  /**
   * Insiste con abrir mientras el puerto siga sin ser de nadie. Ver OPEN_ATTEMPTS:
   * el que lo tenía puede estar todavía soltándolo.
   *
   * Devuelve si quedó utilizable. No tira: quien llama no tiene nada mejor que
   * hacer con el error que lo que se hace acá, y una excepción desde la cola de
   * operaciones termina en una promesa sin dueño.
   */
  openWithRetry = async () => {
    for (let attempt = 1; attempt <= OPEN_ATTEMPTS; attempt += 1) {
      if (this.suspended || !this.hasOwners) return false;
      // Alguien lo abrió mientras esperábamos; se toma como está.
      if (this.port.readable) return true;
      try {
        await this.port.open({ baudRate: this.baudRate }); // eslint-disable-line no-await-in-loop
        return true;
      } catch (error) {
        if (attempt === OPEN_ATTEMPTS) {
          logProtocolError('', `No se pudo abrir el puerto después de ${OPEN_ATTEMPTS} intentos:`, error.message);
          return false;
        }
        await delay(OPEN_RETRY_MS); // eslint-disable-line no-await-in-loop
      }
    }
    return false;
  }

  closePort = async () => {
    if (!this.opened) return;
    this.opened = false;
    await this.stopReading();
    this.releaseWriter();
    // Ver CLOSE_STEP_TIMEOUT_MS: cerrar es lo último y no puede dejar colgado a
    // quien esperaba el puerto libre.
    await guard(() => this.port.close(), 'port.close()', this.closeTimeout);
  }

  stopReading = async () => {
    const reader = this.reader;
    if (!reader) return;
    this.reader = null;
    await guard(() => reader.cancel(), 'reader.cancel()', this.closeTimeout);
    try {
      reader.releaseLock();
    } catch (error) { /* cancel() ya lo soltó */ }
    // El puerto no se puede cerrar hasta que el loop haya salido de veras.
    await guard(() => this.readLoop, 'loop de lectura', this.closeTimeout);
    this.readLoop = null;
  }

  /**
   * Solo suelta el lock, sin cerrar el stream.
   *
   * close() sobre el writer espera a que el stream termine de cerrarse del lado
   * del dispositivo, y eso puede no pasar nunca con la placa enchufada. Para
   * cerrar el puerto alcanza con que writable esté desbloqueado, que es lo que
   * pide la spec, así que cerrarlo era esperar de más sin necesidad.
   */
  releaseWriter = () => {
    if (!this.writer) return;
    try {
      this.writer.releaseLock();
    } catch (error) { /* ya soltado */ }
    this.writer = null;
  }

  /**
   * Un solo loop de lectura para todos. Los bytes van crudos al separador: recién
   * ahí se sabe qué es trama y qué es texto, y solo el texto se decodifica.
   */
  read = async () => {
    if (!this.port.readable || this.port.readable.locked) return;
    let reader;
    try {
      reader = this.port.readable.getReader();
    } catch (error) {
      return;
    }
    this.reader = reader;
    try {
      for (;;) {
        const { value, done } = await reader.read(); // eslint-disable-line no-await-in-loop
        if (done) break;
        if (value) this.dispatch(value);
      }
    } catch (error) {
      // Se cancela el reader al cerrar, y la placa desenchufada corta la lectura:
      // ninguno de los dos es algo que informar.
    } finally {
      try { reader.releaseLock(); } catch (error) { /* ya soltado */ }
      if (this.reader === reader) this.reader = null;
    }
  }

  dispatch = (chunk) => {
    const result = this.splitter.push(chunk);
    if (result.text) this.text.next(result.text);
    result.frames.forEach((frame) => this.frames.next(frame));
  }

  // Bytes tal cual: las tramas son binarias. Un string se manda en UTF-8.
  write = async (data) => {
    if (!this.opened || !this.port.writable) return false;
    try {
      if (!this.writer) this.writer = this.port.writable.getWriter();
      await this.writer.write(typeof data === 'string' ? new TextEncoder().encode(data) : data);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Cambia la velocidad del puerto. Si estaba abierto se cierra y se vuelve a
   * abrir, que es la única manera de cambiarla en WebSerial; los owners siguen
   * anotados, así que nadie tiene que volver a pedirlo.
   *
   * Lo usa el ping para buscar a la placa a otra velocidad: el Serial.begin() del
   * usuario le cambia la UART también al firmware.
   */
  setBaudRate = (baudRate) => this.enqueue(async () => {
    if (!baudRate || this.baudRate === baudRate) return;
    this.baudRate = baudRate;
    if (!this.opened) return;
    await this.closePort();
    await this.openPort();
  })

  /**
   * Le presta el puerto entero a otro -- el flasheo, que abre y cierra por su
   * cuenta -- y lo recupera después.
   *
   * Los owners se mantienen mientras tanto: el monitor y el ping siguen anotados,
   * así que al terminar el puerto vuelve solo. Si el flasheo falla, el reabrir va
   * igual en el finally, porque dejar la sesión cerrada con owners adentro sería
   * un puerto que nadie vuelve a abrir hasta recargar la página.
   */
  withExclusiveAccess = async (operation) => {
    await this.enqueue(() => this.closePort());
    this.suspended = true;
    try {
      return await operation();
    } finally {
      this.suspended = false;
      await this.enqueue(() => this.openPort());
    }
  }
}

/**
 * Una sesión por puerto físico. WebSerial devuelve siempre el mismo objeto
 * SerialPort para el mismo puerto, así que sirve de clave; el WeakMap deja que la
 * sesión de una placa desenchufada se junte sola con el objeto.
 */
const sessions = new WeakMap();

export const getSerialSession = (port, options) => {
  if (!port) return null;
  if (!sessions.has(port)) sessions.set(port, new SerialSession(port, options));
  return sessions.get(port);
};

// Para los tests, que necesitan empezar sin nada de la prueba anterior.
export const forgetSerialSession = (port) => sessions.delete(port);

export default SerialSession;
